import { gsap } from "gsap";
import { Event } from "event-system";
import { Container, Graphics, IDestroyOptions, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { GameFont } from "../../../../common/GameFont";
import { MarketplaceListItem } from "../models/map/MarketplaceListItem";
import { MarketplaceMapCardLoader } from "./MarketplaceMapCardLoader";
import { StringFormatExtention } from "../../../../../../extensions/StringFormatExtention";
import { IMarketplaceViewModel } from "../models/IMarketplaceViewModel";
import { NoCacheDynamicPackage } from "../../../../common/resources/NoCacheDynamicPackage";
import { HBox } from "../../../../common/components/ui/HBox";
import { LabelContainer } from "./LabelContainer";
import { LabelType } from "./LabelType";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { MarketplaceResourcePackage } from "../../resources/MarketplaceResourcePackage";
import { MonField } from "../../../../common/components/MonField";
import { VBox } from "../../../../common/components/ui/VBox";

export class MarketplaceCard extends Container {
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    public readonly listItem: MarketplaceListItem;
    public readonly marketplaceViewModel: IMarketplaceViewModel;
    public readonly onCardClick: Event;
    private buildingBg: Sprite;
    private building: Sprite;
    private road: Sprite;
    private address: Text;
    private level: Text;
    private monField: MonField;
    private pricesContainer: Container;
    private price: Text;
    private glow: Sprite;
    private highlighted: boolean;
    private marketplaceCardClicked: boolean;
    private downMousePosX: number;
    private loader: MarketplaceMapCardLoader;
    private cardDynamicResources: NoCacheDynamicPackage;
    private cardAnimation: gsap.core.Timeline;
    private iconsVbox: VBox;
    private discountCouponIcon: Sprite;
    private currentCardPurchaseType: cardPurchaseType;

    get isHighlighted(): boolean {
        return this.highlighted;
    }

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        commonResources: CommonResourcesPackage,
        listItem: MarketplaceListItem,
        marketplaceViewModel: IMarketplaceViewModel
    ) {
        super();
        this.marketplaceResources = marketplaceResources;
        this.commonResources = commonResources;
        this.highlighted = false;
        this.listItem = listItem;
        this.marketplaceViewModel = marketplaceViewModel;
        this.onCardClick = new Event();
        this.cardAnimation = new gsap.core.Timeline();
        this.currentCardPurchaseType = this.listItem.price.mon != null ? cardPurchaseType.MON : cardPurchaseType.USD;
        this.build();
        this.showCard();
        this.on("pointerdown", this.cardTouchStartHandle, this);
        this.on("pointerup", this.cardTouchEndHandle, this);
        this.on("pointermove", this.cardTouchMoveHandle, this);
    }

    private build(): void {
        this.glow = new Sprite(this.marketplaceResources.assets_views_marketplace_glow_png);
        this.glow.anchor.set(0.5);
        this.addChild(this.glow);

        this.buildingBg = new Sprite(this.marketplaceResources.assets_views_marketplace_nft_card_png);
        this.buildingBg.anchor.set(0.5);
        this.buildingBg.alpha = 0;
        this.buildingBg.position.y = this.glow.position.y;
        this.addChild(this.buildingBg);

        const mask = new Graphics();
        mask.beginFill(0xffffff);

        const rectWidth = this.buildingBg.width;
        const rectHeight = this.buildingBg.height;
        const rectX = -rectWidth / 2;
        const rectY = -rectHeight / 2;

        const cornerRadius = 40;

        mask.moveTo(rectX + cornerRadius, rectY);
        mask.lineTo(rectX + rectWidth - cornerRadius, rectY);
        mask.arcTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + cornerRadius, cornerRadius);
        mask.lineTo(rectX + rectWidth, rectY + rectHeight - cornerRadius);
        mask.arcTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - cornerRadius, rectY + rectHeight, cornerRadius);
        mask.lineTo(rectX + cornerRadius, rectY + rectHeight);
        mask.arcTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - cornerRadius, cornerRadius);
        mask.lineTo(rectX, rectY + cornerRadius);
        mask.arcTo(rectX, rectY, rectX + cornerRadius, rectY, cornerRadius);

        mask.endFill();

        this.building = new Sprite(Texture.EMPTY);
        this.building.anchor.set(0.5, 0.5);
        this.building.position.y = -this.building.height * 0.28;
        this.building.mask = mask;
        this.addChild(this.building);
        this.addChild(mask);

        this.road = new Sprite(this.marketplaceResources.assets_views_marketplace_road_png);
        this.road.anchor.set(0.5);
        this.road.position.y = this.building.position.y + this.building.height * 0.5 + this.road.height * 0.5;
        this.road.mask = mask;
        this.addChild(this.road);

        this.level = new Text(`Rank `, new LevelTextStyle());
        this.level.anchor.set(0.5, 0.5);
        this.level.angle = -8;
        this.level.skew._x = -0.087;
        this.level.position.y = -187.3;
        this.addChild(this.level);

        this.address = new Text(` `, new StreetTextStyle());
        this.address.anchor.set(0.5, 0.5);
        this.address.position.y = this.buildingBg.height * 0.27;
        this.addChild(this.address);

        this.iconsVbox = new VBox(-50);
        this.iconsVbox.position.set(40, -249);
        this.addChild(this.iconsVbox);

        if (this.listItem.building.supportCoupon) {
            this.discountCouponIcon = new Sprite(this.marketplaceResources.assets_views_marketplace_discount_coupons_glow_png);
            this.discountCouponIcon.alpha = 0;
            this.iconsVbox.addChild(this.discountCouponIcon);
        }

        this.buildPrice();

        if (this.listItem.sold) {
            const soldSprite = new Sprite(this.marketplaceResources.assets_views_marketplace_sold_png);
            soldSprite.anchor.set(0.5);
            soldSprite.position.y = 18;
            this.addChild(soldSprite);
        }

        const labelHBox = new HBox(11);
        if (this.listItem.building.tokensForPurchasing) {
            const tokenConstLabel = new LabelContainer(this.marketplaceResources, this.commonResources, LabelType.TOKEN, parseFloat(this.listItem.building.tokensForPurchasing));
            labelHBox.addChild(tokenConstLabel);
        }
        if (this.listItem.building.piggyMultiplier != 1) {
            const piggyMultiplierLabel = new LabelContainer(this.marketplaceResources, this.commonResources, LabelType.PIGGY, this.listItem.building.piggyMultiplier);
            labelHBox.addChild(piggyMultiplierLabel);
        }
        if (labelHBox.children.length === 1) {
            labelHBox.position.set(-58.5, -299);
        } else if (labelHBox.children.length === 2) {
            labelHBox.position.set(-144, -299);
        }
        this.addChild(labelHBox);

        this.loader = new MarketplaceMapCardLoader(this.marketplaceResources);
        this.loader.scale.set(1.03);
        this.addChild(this.loader);
    }

    private buildPrice(): void {
        switch (this.currentCardPurchaseType) {
            case cardPurchaseType.USD:
                this.pricesContainer = new Container();
                this.pricesContainer.y = 177;
                this.addChild(this.pricesContainer);

                this.price = new Text(` `, new PriceUnselectedTextStyle());
                this.price.anchor.set(0.5, 0.5);
                this.pricesContainer.addChild(this.price);
                if (this.listItem.sold) {
                    this.price.style = new SoldCardTextStyle();
                }
                if (this.listItem.my) {
                    this.price.style = new PriceUserTextStyle();
                }
                break;
            case cardPurchaseType.MON:
                if (!this.listItem.sold) {
                    const monIconWithGlow = new Sprite(this.marketplaceResources.assets_views_marketplace_mon_glow_icon_png);
                    this.iconsVbox.addChild(monIconWithGlow);
                    monIconWithGlow.x = -7;
                }
                this.monField = new MonField(this.listItem.price.mon, this.marketplaceResources.assets_views_marketplace_mon_icon_png, new monTextStyle(), 10);
                this.monField.position.set(-this.monField.width * 0.5, 160);
                this.addChild(this.monField);
                break;
        }
    }

    private async showCard(): Promise<void> {
        this.loader.show();
        this._internalEventMode = "none";
        this.glow.alpha = 0;
        this.updateCardInformation();
        try {
            await this.loadBuildingTexture();
            this.building.texture = this.cardDynamicResources.resource<Texture>(this.listItem.building.imageUrl);
        } catch (error) {
            this.building.texture = Texture.EMPTY;
        } finally {
            this.building.width = 304;
            this.building.height = 304;
            this.building.position.y = -this.building.height * 0.28;
            this.road.position.y = this.building.position.y + this.building.height * 0.5 + this.road.height * 0.5;
            this.loader.hide();
            this._internalEventMode = "dynamic";
        }
        if (this.listItem.building.supportCoupon && this.discountCouponIcon != null) {
            this.cardAnimation.to(this.discountCouponIcon, { alpha: 1, duration: 0.15 });
            await this.cardAnimation.play();
            this.cardAnimation.clear();
        }
    }

    public async showGlow(): Promise<void> {
        this.cardAnimation.to(this.glow, { alpha: 1, duration: 0.1 });
        await this.cardAnimation.play();
        this.highlighted = true;
        this.cardAnimation.clear();
    }

    public async hideGlow(): Promise<void> {
        this.cardAnimation.to(this.glow, { alpha: 0, duration: 0.1 });
        await this.cardAnimation.play();
        this.highlighted = false;
        this.cardAnimation.clear();
    }

    private updateCardInformation(): void {
        this.level.text = `Rank ${this.listItem.building.level}`;
        this.address.text = `${this.listItem.building.address}`;
        if (this.address.width >= this.road.width * 0.95) {
            this.address.scale.set(this.road.width * 0.95 / this.address.width);
        }
        switch (this.currentCardPurchaseType) {
            case cardPurchaseType.USD:
                this.price.text = `$${StringFormatExtention.formatWithComma(this.listItem.price.usd)}`;
                this.pricesContainer.x = -this.pricesContainer.width * 0.5 + this.price.width * 0.5;
                break;
            case cardPurchaseType.USD:
                this.monField.updatePrice(this.listItem.price.mon);
                break;
        }
    }

    private async loadBuildingTexture(): Promise<void> {
        function getUniqueId() {
            const dateString = Date.now().toString(36);
            const randomness = Math.random().toString(36).substr(2);
            return dateString + randomness;
        }
        this.cardDynamicResources = new NoCacheDynamicPackage("mpBuilding" + getUniqueId());
        this.cardDynamicResources.add(this.listItem.building.imageUrl);
        await this.cardDynamicResources.load();
    }

    private async cardTouchStartHandle(event: any): Promise<void> {
        this.marketplaceCardClicked = true;
        this.downMousePosX = Math.ceil(event.data.global.x);
    }

    private async cardTouchMoveHandle(event: any): Promise<void> {
        let currentMousePosX: number = Math.ceil(event.data.global.x);
        if (Math.abs(this.downMousePosX - currentMousePosX) > 20) {
            this.marketplaceCardClicked = false;
        }
    }

    private async cardTouchEndHandle(): Promise<void> {
        if (this.marketplaceCardClicked) {
            this.onCardClick.emit();
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerdown", this.cardTouchStartHandle, this);
        this.off("pointerup", this.cardTouchEndHandle, this);
        this.off("pointermove", this.cardTouchMoveHandle, this);
        this.onCardClick.removeAll();
        this.cardAnimation?.kill();
        this.cardDynamicResources.destroy();
        super.destroy(options);
    }
}

enum cardPurchaseType {
    USD = "USD",
    MON = "MON"
}

class StreetTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36
        }));
    }
}

class PriceUserTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#a5fb09",
                "#1ad015"
            ],
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}

class PriceUnselectedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#ffe302",
                "#ff9024"
            ],
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}

class SoldCardTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#ff4955",
                "#ff04b8"
            ],
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super({
            dropShadow: true,
            dropShadowAlpha: 0.4,
            dropShadowAngle: 0,
            dropShadowBlur: 2,
            dropShadowDistance: 2,
            fill: "#ffffff",
            fontFamily: "COMICZ",
            strokeThickness: 1,
            fontSize: 34.33
        });
    }
}

class monTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#FFE302",
                "#FF9024"
            ],
            fillGradientStops: [0.2],
            fontSize: 40,
            align: "center",
        });
    }
}